import { useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { SunMark } from "@/components/Logo";

const MAIN_OPTIONS = [
  {
    label: "Explore the Website",
    href: "/",
    testid: "widget-nav-explore-the-website",
  },
  {
    label: "Books & Workbooks",
    href: "/books",
    testid: "widget-nav-books-workbooks",
  },
  {
    label: "Free Resources",
    href: "/free-resources",
    testid: "widget-nav-free-resources",
  },
  {
    label: "Facilitator Training & Certification",
    href: "/facilitators/certification",
    testid: "widget-nav-facilitator-training-certification",
  },
  {
    label: "Licensing & Permissions",
    href: "/licensing",
    testid: "widget-nav-licensing-permissions",
  },
  {
    label: "Speaking & Signings",
    href: "/speaking",
    testid: "widget-nav-speaking-signings",
  },
];

const MORE_OPTIONS = [
  {
    label: "Group Copies",
    href: "/group-copies",
    testid: "widget-more-group-copies",
  },
  {
    label: "Message Chaplain Derrick",
    href: "/ask",
    testid: "widget-more-message-chaplain-derrick",
  },
];

export default function MessageWidget() {
  const [open, setOpen] = useState(false);

  return (
    <div className="fixed bottom-5 right-5 z-40 hidden flex-col items-end gap-3 md:flex">
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            role="dialog"
            aria-label="Guided assistance"
            data-testid="message-widget-panel"
            className="w-[min(380px,calc(100vw-2.5rem))] max-h-[75vh] overflow-y-auto bg-white border border-line rounded-lg shadow-[0_24px_60px_-24px_rgba(11,21,34,0.45)] origin-bottom-right"
          >
            <div className="flex items-center justify-between bg-navy-deep rounded-t-lg px-5 py-4">
              <div className="flex items-center gap-2.5">
                <SunMark className="w-5 h-5 text-gold shrink-0" />
                <p className="font-serif text-lg text-ivory leading-tight">
                  How Can We Help?
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="message-widget-close"
                className="text-ivory/70 hover:text-gold transition-colors duration-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5">
              <p className="text-[10px] uppercase tracking-[0.18em] text-gold-hover font-medium">
                From Diagnosis to Goodbye&trade; &middot; Guided Assistance
              </p>
              <p className="mt-3 text-sm text-stone2 leading-relaxed">
                Choose a topic and we&rsquo;ll guide you to the right place
                &mdash;or leave a message for Chaplain Derrick.
              </p>

              <div className="mt-5 flex flex-col gap-2">
                {MAIN_OPTIONS.map((o) => (
                  <Link
                    key={o.href}
                    to={o.href}
                    data-testid={o.testid}
                    className="group flex items-center justify-between gap-3 w-full bg-ivory border border-line rounded-md px-4 py-3 text-sm font-medium text-navy hover:border-gold hover:bg-gold-light/40 transition-colors duration-200"
                  >
                    {o.label}
                    <ArrowRight className="w-4 h-4 text-gold group-hover:translate-x-0.5 transition-transform duration-200" />
                  </Link>
                ))}
              </div>

              <p className="mt-6 text-[11px] uppercase tracking-[0.18em] text-mist">
                More Options
              </p>
              <div className="mt-3 flex flex-col gap-2">
                {MORE_OPTIONS.map((o) => (
                  <Link
                    key={o.href}
                    to={o.href}
                    data-testid={o.testid}
                    className="group flex items-center justify-between gap-3 w-full bg-ivory border border-line rounded-md px-4 py-3 text-sm font-medium text-navy hover:border-gold hover:bg-gold-light/40 transition-colors duration-200"
                  >
                    {o.label}
                    <ArrowRight className="w-4 h-4 text-gold group-hover:translate-x-0.5 transition-transform duration-200" />
                  </Link>
                ))}
              </div>

              <p className="mt-6 text-xs text-mist leading-relaxed">
                This website is not monitored for emergencies or
                mental-health crises. If you or someone you love is in
                immediate crisis, please contact your care team or call your
                local emergency number.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        data-testid="message-widget-launcher"
        className="inline-flex items-center gap-2.5 bg-navy text-ivory pl-4 pr-5 py-3 rounded-full shadow-[0_16px_36px_-16px_rgba(11,21,34,0.6)] hover:bg-navy-light hover:-translate-y-0.5 transition-all duration-200"
      >
        <SunMark className="w-5 h-5 text-gold shrink-0" />
        <span className="text-sm font-medium">Message Chaplain Derrick</span>
      </button>
    </div>
  );
}
